import React, { useEffect } from 'react';
import _ from 'lodash';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-fetching-library';
import { updateOrderAction } from '../../../../services/request';
import { updateOrder } from '../../../../services/redux-actions';
import { Box, Tabs, Tab, Button, Badge } from '@mui/material';
import Scrollbar from '../../../../components/Scrollbar';
import OrderTab from './tabs/OrderTab/OrderTab';
import ReservationDataForm from './tabs/ReservationDataTab/ReservationDataForm';
import ParticipantDetails from './tabs/ParticipanDetailsTab/ParticipantDetails';
import DocumentsAndNotifications from './tabs/DocumentAndNotificationsTab/DocumentsAndNotifications';
import SettlementTab from './tabs/SettlementTab/SettlementTab';
import ChangeHistory from './tabs/ChangeHistoryTab/ChangeHistory';
import Iconify from '../../../../components/Iconify';
import toast from 'react-hot-toast';

const tabInputs = {
  orderTab: ['identifier', 'participant_name', 'participant_street_address', 'participant_postal_code', 'participant_city', 'guardian_name', 'guardian_correspondence_email', 'guardian_street_address', 'guardian_postal_code', 'guardian_city', 'event', 'camp'],
  settlementTab: ['contractor_name', 'contractor_street_address', 'contractor_postal_code', 'contractor_city', 'contractor_correspondence_email']
};

const validationSchema = Yup.object({
  // OrderTab
  identifier: Yup.string().required('To pole nie może być puste'),
  participant_name: Yup.string().required('To pole nie może być puste'),
  participant_street_address: Yup.string().required('To pole nie może być puste'),
  participant_postal_code: Yup.string().required('To pole nie może być puste'),
  participant_city: Yup.string().required('To pole nie może być puste'),
  guardian_name: Yup.string().required('To pole nie może być puste'),
  guardian_correspondence_email: Yup.string().email('Proszę podać poprawny adres e-mail'),
  guardian_street_address: Yup.string().required('To pole nie może być puste'),
  guardian_postal_code: Yup.string().required('To pole nie może być puste'),
  guardian_city: Yup.string().required('To pole nie może być puste'),
  event: Yup.string().required('To pole nie może być puste'),
  camp: Yup.string().required('To pole nie może być puste'),
  // SettlementTab
  contractor_name: Yup.string(),
  contractor_street_address: Yup.string(),
  contractor_postal_code: Yup.string(),
  contractor_city: Yup.string(),
  contractor_correspondence_email: Yup.string().email('Proszę podać poprawny adres e-mail')
});


export const OLFullScreenView = ({ updateOrders, refFormik }) => {
  const dispatch = useDispatch();
  const { order } = useSelector(state => state.orderData);
  const { loading, mutate } = useMutation(updateOrderAction);
  const [currentTab, setCurrentTab] = React.useState(0);
  const [validationErros, setValidationErrors] = React.useState({ orderTab: false, settlementTab: false });

  const handleSubmit = async (orderId, values) => {
    const { error, payload } = await mutate(orderId, values);
    if (!error) {
      formik.resetForm({
        values: { ...values,
          positions: payload.products.map(p => ({ status: 'OLD', ...p })),
          final_payment_date: payload.final_payment_date,
          advance_payment_date: payload.advance_payment_date,
          shared_accommodation_changed: payload.shared_accommodation_changed }
      });
      dispatch(updateOrder(payload));
      updateOrders(payload);
      toast.success('Zaktualizowano dane zamówienia');
    } else {
      // eslint-disable-next-line no-lonely-if
      if (_.isArray(payload)) {
        toast.error(payload[0]);
      } else {
        toast.error('Nie udało się zapisać zmian');
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      // OrderTab
      participant_name: _.isNil(order.participant_name) ? '' : order.participant_name,
      participant_birth_date: _.isNil(order.participant_birth_date) ? null : order.participant_birth_date,
      participant_pesel: _.isNil(order.participant_pesel) ? '' : order.participant_pesel,
      participant_gender: _.isNil(order.participant_gender) ? '' : order.participant_gender,
      participant_street_address: _.isNil(order.participant_street_address) ? '' : order.participant_street_address,
      participant_postal_code: _.isNil(order.participant_postal_code) ? '' : order.participant_postal_code,
      participant_city: _.isNil(order.participant_city) ? '' : order.participant_city,
      guardian_name: _.isNil(order.guardian_name) ? '' : order.guardian_name,
      guardian_nip: _.isNil(order.guardian_nip) ? '' : order.guardian_nip,
      guardian_phone: _.isNil(order.guardian_phone) ? '' : order.guardian_phone,
      guardian_additional_phone: _.isNil(order.guardian_additional_phone) ? '' : order.guardian_additional_phone,
      guardian_correspondence_email: _.isNil(order.guardian_correspondence_email) ? '' : order.guardian_correspondence_email,
      guardian_street_address: _.isNil(order.guardian_street_address) ? '' : order.guardian_street_address,
      guardian_postal_code: _.isNil(order.guardian_postal_code) ? '' : order.guardian_postal_code,
      guardian_city: _.isNil(order.guardian_city) ? '' : order.guardian_city,
      identifier: _.isNil(order.identifier) ? '' : order.identifier,
      created_at: _.isNil(order.created_at) ? '' : order.created_at,
      resort: _.isNil(order.resort) ? '' : order.resort,
      event: _.isNil(order.event) ? '' : order.event,
      camp: _.isNil(order.camp) ? '' : order.camp,
      transport_departure_object: _.isNil(order.transport_departure_object) ? '' : order.transport_departure_object,
      transport_return_object: _.isNil(order.transport_return_object) ? '' : order.transport_return_object,
      transport_reserve_object: _.isNil(order.transport_reserve_object) ? '' : order.transport_reserve_object,
      comments: _.isNil(order.comments) ? '' : order.comments,
      positions: _.isNil(order.products) ? [] : order.products.map(p => ({ status: 'OLD', ...p })),
      // ReservationDataForm
      rodo_terms: order.rodo_terms,
      other_terms: order.other_terms,
      marketing_consents_email: order.marketing_consents_email,
      marketing_consents_sms: order.marketing_consents_sms,
      marketing_consents_phone: order.marketing_consents_phone,
      participation_terms: order.participation_terms,
      info_form: order.info_form,
      source_of_offer: _.isNil(order.source_of_offer) ? '' : order.source_of_offer,
      form_of_communication: _.isNil(order.form_of_communication) ? '' : order.form_of_communication,
      return_of_health_card: order.return_of_health_card,
      no_return_of_card: order.no_return_of_card,
      no_return_of_card_description: _.isNil(order.no_return_of_card_description) ? '' : order.no_return_of_card_description,
      return_agreement: order.return_agreement,
      // annex_return: order.annex_return,
      // accommodated: order.accommodated,
      family_card_number: _.isNil(order.family_card_number) ? '' : order.family_card_number,
      parish_name: _.isNil(order.parish_name) ? '' : order.parish_name,
      // ParticipantDetails
      diet: _.isNil(order.diet) ? '' : order.diet,
      participant_diet: _.isNil(order.participant_diet) ? '' : order.participant_diet,
      participant_regular_medication: _.isNil(order.participant_regular_medication) ? '' : order.participant_regular_medication,
      participant_diseases_and_allergies: _.isNil(order.participant_diseases_and_allergies) ? '' : order.participant_diseases_and_allergies,
      participant_behavior: _.isNil(order.participant_behavior) ? '' : order.participant_behavior,
      participant_other_comments: _.isNil(order.participant_other_comments) ? '' : order.participant_other_comments,
      participant_siblings_and_friends: _.isNil(order.participant_siblings_and_friends) ? '' : order.participant_siblings_and_friends,
      shared_accommodation_info: _.isNil(order.shared_accommodation_info) ? '' : order.shared_accommodation_info,
      show_shared_accommodation_confirmation: _.isNil(order.show_shared_accommodation_confirmation) ? '' : order.show_shared_accommodation_confirmation,
      // DocumentsAndNotifications
      automatic_notifications: order.automatic_notifications,
      manual_notifications: order.manual_notifications,
      // SettlementTab
      contractor_name: _.isNil(order.contractor_name) ? '' : order.contractor_name,
      contractor_nip: _.isNil(order.contractor_nip) ? '' : order.contractor_nip,
      contractor_phone: _.isNil(order.contractor_phone) ? '' : order.contractor_phone,
      contractor_correspondence_email: _.isNil(order.contractor_correspondence_email) ? '' : order.contractor_correspondence_email,
      contractor_street_address: _.isNil(order.contractor_street_address) ? '' : order.contractor_street_address,
      contractor_postal_code: _.isNil(order.contractor_postal_code) ? '' : order.contractor_postal_code,
      contractor_city: _.isNil(order.contractor_city) ? '' : order.contractor_city,
      invoice: '',
      advance_payment_date: _.isNil(order.advance_payment_date) ? null : order.advance_payment_date,
      final_payment_date: _.isNil(order.final_payment_date) ? null : order.final_payment_date,
      is_invoice_issued: order.is_invoice_issued,
      is_invoice_paid: order.is_invoice_paid,
      automatic_advance_reminder_notifications: _.isNil(order.automatic_advance_reminder_notifications) ? true : order.automatic_advance_reminder_notifications,
      way_of_sending_contract: _.isNil(order.way_of_sending_contract) ? '' : order.way_of_sending_contract,
      documents_notes: _.isNil(order.documents_notes) ? '' : order.documents_notes,
      friends: _.isNil(order.friends) ? '' : order.friends,
      friends_display: _.isNil(order.friends_display) ? '' : order.friends_display,
      siblings: _.isNil(order.siblings) ? '' : order.siblings,
      siblings_display: _.isNil(order.siblings_display) ? '' : order.siblings_display,
      discount_code: _.isNil(order.discount_code) ? '' : order.discount_code,
      friends_promotion_choice: _.isNil(order.friends_promotion_choice) ? '' : order.friends_promotion_choice,
      team_promotion: _.isNil(order.team_promotion) ? '' : order.team_promotion,
      team_promotion_display: _.isNil(order.team_promotion_display) ? '' : order.team_promotion_display,
      accounting_notes: _.isNil(order.accounting_notes) ? '' : order.accounting_notes,
      want_invoice: order.want_invoice,
      tshirt_size: _.isNil(order.tshirt_size) ? '' : order.tshirt_size,
      qualification_card_returned: _.isNil(order.qualification_card_returned) ? false : order.qualification_card_returned,
      attachments: _.isNil(order.attachments) ? [] : order.attachments,
      classification: _.isNil(order.classification) ? '' : order.classification,
      classification_details: _.isNil(order.classification_details) ? '' : order.classification_details,
      shared_accommodation_changed: _.isNil(order.shared_accommodation_changed) ? false : order.shared_accommodation_changed,
      reservation_notes: _.isNil(order.reservation_notes) ? '' : order.reservation_notes,
      accommodation_event_group: _.isNil(order.accommodation_event_group) ? null : order.accommodation_event_group,
      payment_terms_accepted: order.payment_terms_accepted,
      client_status: _.isNil(order.client_status) ? '' : order.client_status,
      camp_recommended_by_client_friend: _.isNil(order.camp_recommended_by_client_friend) ? null : order.camp_recommended_by_client_friend,
      second_guardian_name_and_surname: _.isNil(order.second_guardian_name_and_surname) ? '' : order.second_guardian_name_and_surname,
      child_english_communicative: order.child_english_communicative,
      quick_payment_promotion: order.quick_payment_promotion,
      consents: _.isNil(order.consents) ? [] : order.consents,
      message_from_office: _.isNil(order.message_from_office) ? '' : order.message_from_office

    },
    validationSchema,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: values => {
      console.log(values);
      if (values.classification == '') {
        values.classification = null;
      }
      handleSubmit(order.id, values);
    }
  });

  useEffect(() => {
    let orderTabCount = 0;
    let settlementTabCount = 0;

    if (!_.isEmpty(formik.errors)) {
      const keys = Object.keys(formik.errors);

      for (const key of keys) {
        if (tabInputs.orderTab.includes(key)) {
          orderTabCount++;
        } else if (tabInputs.settlementTab.includes(key)) {
          settlementTabCount++;
        }
      }

      toast.error('Upewnij się czy wszystkie pola zostały poprawnie wypełnione');
    }

    if (orderTabCount > 0) {
      setValidationErrors(prev => ({ ...prev, orderTab: true }));
    } else {
      setValidationErrors(prev => ({ ...prev, orderTab: false }));
    }

    if (settlementTabCount > 0) {
      setValidationErrors(prev => ({ ...prev, settlementTab: true }));
    } else {
      setValidationErrors(prev => ({ ...prev, settlementTab: false }));
    }
  }, [formik.errors]);

  refFormik.current = formik;

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Tabs sx={{ ml: 1 }} value={currentTab} onChange={(e, value) => setCurrentTab(value)}>
          <Tab
            label="zamówienie"
            icon={<Badge color="error" variant="dot" invisible={!validationErros.orderTab}><Iconify icon={'icon-park-outline:transaction-order'} width={20} height={20} /></Badge>} />
          <Tab label="dane rezerwacji" icon={<Iconify icon={'ri:hotel-fill'} width={20} height={20} />} />
          <Tab label="uczestnik - szczegóły" icon={<Iconify icon={'tabler:mood-kid'} width={20} height={20} />} />
          <Tab label="dokumenty i powiadomienia" icon={<Iconify icon={'mdi:file-document-multiple'} width={20} height={20} />} />
          <Tab
            label="rozliczenia"
            icon={<Badge color="error" variant="dot" invisible={!validationErros.settlementTab}><Iconify icon={'mdi:currency-usd'} width={20} height={20} /></Badge>} />
          <Tab label="historia zmian" icon={<Iconify icon={'mdi:clipboard-text-clock'} width={20} height={20} />} />
        </Tabs>
        <Box sx={{ mt: 2, mb: 2 } } display="flex" justifyContent="center" alignItems="center">
          <Button
            variant="contained"
            disabled={!formik.dirty || loading}
            onClick={formik.handleSubmit}
            sx={{ width: '50%' }}
          >
            Zapisz zmiany
          </Button>
        </Box>
      </Box>
      <Scrollbar>
        { currentTab === 0
          ? <OrderTab
            formik={formik}
          />
          : currentTab === 1
            ? <ReservationDataForm
              formik={formik}
            />
            : currentTab === 2
              ? <ParticipantDetails
                formik={formik}
              />
              : currentTab === 3
                ? <DocumentsAndNotifications
                  updateOrders={updateOrders}
                  formik={formik}
                />
                : currentTab === 4
                  ? <SettlementTab
                    formik={formik}
                  />
                  : currentTab === 5
                    ? <ChangeHistory />
                    : null}
      </Scrollbar>
    </>
  );
};
